import { useEffect, useRef, useState } from 'react'
import { Html, Line } from '@react-three/drei'
import { track } from '@vercel/analytics'
import useGame from '../stores/useGame.jsx'
import * as THREE from 'three'
import { loadPOIData, getPOIDataByNode } from '../utils/poiDataLoader.js'

// SEC006 "Weapon Stores" POI markers — standalone component, intentionally
// excluded from MarkerRepulsion. Labels use wrapperClass="poi-html-portal-sec006"
// which MarkerRepulsion does NOT query.
// Offsets (3D world-space) are loaded from /POI_Markers_Offset.json at runtime.

export function POI_Markers_SEC006({ nodes }) {
    const [poiGroups, setPoiGroups] = useState([])
    const [poiDataMap, setPoiDataMap] = useState(null)
    const [offsetMap, setOffsetMap] = useState({})
    const [hoveredIndex, setHoveredIndex] = useState(null)
    const [selectedPoiName, setSelectedPoiName] = useState(null)
    const [fadeState, setFadeState] = useState('in') // 'in' | 'out'
    const [visible, setVisible] = useState(false)
    const fadeDuration = 400 // ms

    const selectedSec = useGame((s) => s.selectedSec)
    const poiVisible = useGame((s) => s.poiVisible)
    const deviceType = useGame((s) => s.deviceType)

    // Load CSV POI data
    useEffect(() => {
        loadPOIData().then(data => setPoiDataMap(data))
    }, [])

    // Load offset JSON
    useEffect(() => {
        fetch('/POI_Markers_Offset.json')
            .then(r => r.json())
            .then(data => setOffsetMap(data))
            .catch(() => {})
    }, [])

    // Hardcoded world positions for SEC006 POI anchors (from GLB export).
    // These are mirrored through the PJDAM_Solo_Ctrl group center [1.942, -0.721, -0.796]
    // to account for the 180° X rotation applied when SEC006 is active.
    // Formula: newY = 2*(-0.721) - oldY,  newZ = 2*(-0.796) - oldZ
    const MIRRORED_POSITIONS = {
        POI017: new THREE.Vector3(1.8253, -0.6042, -0.772),
        POI018: new THREE.Vector3(1.6810, -0.6043, -0.772),
        POI019: new THREE.Vector3(1.7483, -0.6054, -0.772),
        POI020: new THREE.Vector3(1.9000, -0.6039, -0.772),
        POI021: new THREE.Vector3(1.4777, -0.7912, -0.772),
        POI022: new THREE.Vector3(2.1097, -0.6381, -0.772),
        POI023: new THREE.Vector3(1.6665, -0.6538, -0.772),
        POI024: new THREE.Vector3(2.0028, -0.8341, -0.772),
        POI025: new THREE.Vector3(1.9493, -0.8340, -0.772),
        POI026: new THREE.Vector3(2.0572, -0.6333, -0.772),
        POI027: new THREE.Vector3(1.3683, -0.70, -0.772),
    }

    // Build POI group list from GLB nodes — only SEC006 info-type POIs
    useEffect(() => {
        if (!nodes || !poiDataMap) return

        const list = []
        Object.entries(nodes).forEach(([nodeName, node]) => {
            if (!nodeName.includes('POI') && !nodeName.toUpperCase().includes('POI')) return

            const poiData = getPOIDataByNode(poiDataMap, nodeName)
            if (poiData?.secRef !== 'SEC006') return

            // Use mirrored positions (pre-calculated to match 180° X rotation of PJDAM_Solo_Ctrl)
            const basePosition = MIRRORED_POSITIONS[nodeName]
                ? MIRRORED_POSITIONS[nodeName].clone()
                : new THREE.Vector3()

            list.push({ name: nodeName, node, basePosition, worldPosition: basePosition.clone() })
        })

        setPoiGroups(list)
    }, [nodes, poiDataMap])

    // Offset worldPosition by -1 Y when SEC006 is active (missile lowers)
    useEffect(() => {
        setPoiGroups(prev => prev.map(poi => {
            const offset = selectedSec === 'SEC006' ? -1 : 0
            const newPos = poi.basePosition.clone()
            newPos.y += offset
            return { ...poi, worldPosition: newPos }
        }))
    }, [selectedSec])

    // Show/hide with fade — 1s delay on entry so camera move completes first
    useEffect(() => {
        if (!poiVisible || selectedSec !== 'SEC006') {
            setFadeState('out')
            const t = setTimeout(() => setVisible(false), fadeDuration)
            return () => clearTimeout(t)
        }
        const t = setTimeout(() => {
            setVisible(true)
            setFadeState('in')
        }, 2000)
        return () => clearTimeout(t)
    }, [selectedSec, poiVisible])

    // Track which marker is selected; clear when POI_Window closes or SEC006 is exited
    useEffect(() => {
        const onSelected = (e) => {
            if (e.detail?.poiName) setSelectedPoiName(e.detail.poiName)
        }
        const onDeselected = () => setSelectedPoiName(null)
        window.addEventListener('poi-selected', onSelected)
        window.addEventListener('poi-deselected', onDeselected)
        return () => {
            window.removeEventListener('poi-selected', onSelected)
            window.removeEventListener('poi-deselected', onDeselected)
        }
    }, [])

    // Also clear selected marker when we leave SEC006
    useEffect(() => {
        if (selectedSec !== 'SEC006') setSelectedPoiName(null)
    }, [selectedSec])

    if (!visible) return null

    return (
        <>
            {poiGroups.map((poi, index) => {
                const poiData = poiDataMap ? getPOIDataByNode(poiDataMap, poi.name) : null
                const isHovered = index === hoveredIndex
                const isSelected = selectedPoiName === poi.name
                const isNonClickable = poi.name === 'POI017'
                const fadeClass = fadeState === 'in' ? 'poi-fade-in' : 'poi-fade-out'

                // Resolve label position from offset JSON
                const poiKey = poi.name.match(/POI[_]?(\d+)/i)
                    ? `POI${poi.name.match(/POI[_]?(\d+)/i)[1].padStart(3, '0')}`
                    : poi.name
                const offsetEntry = offsetMap[poiKey]
                const hasOffset = offsetEntry && (offsetEntry.x !== 0 || offsetEntry.y !== 0 || offsetEntry.z !== 0)
                const labelPosition = hasOffset
                    ? new THREE.Vector3(
                        poi.worldPosition.x + offsetEntry.x,
                        poi.worldPosition.y + offsetEntry.y,
                        poi.worldPosition.z + offsetEntry.z
                      )
                    : poi.worldPosition

                const lineOpacity = fadeState === 'out' ? 0 : 1

                return (
                    <group key={`sec006-poi-${index}`}>
                        {/* Connector line: 3D anchor → label position */}
                        {hasOffset && (
                            <Line
                                points={[poi.worldPosition, labelPosition]}
                                color="white"
                                lineWidth={2}
                                transparent
                                opacity={lineOpacity}
                                depthTest={false}
                            />
                        )}
                        <Html
                            zIndexRange={[0, 3500]}
                            wrapperClass="poi-html-portal-sec006"
                            position={labelPosition}
                            occlude={false}
                            center
                        >
                            <div
                                className={`textButtonContainer sec-marker-dynamic sec-solid ${deviceType === 'phone' ? `sec006-marker ${isSelected ? 'sec006-marker--selected' : ''}` : `${isSelected ? 'sec006-poi-selected' : ''}`} ${isNonClickable ? 'sec006-marker--disabled' : ''} ${fadeClass}`}
                                style={{
                                    transition: 'opacity 0.25s ease',
                                    cursor: isNonClickable ? 'default' : 'pointer',
                                    pointerEvents: isNonClickable ? 'none' : 'auto',
                                }}
                                onPointerOver={(e) => { e.stopPropagation(); if (!isNonClickable) setHoveredIndex(index) }}
                                onPointerOut={(e) => { e.stopPropagation(); setHoveredIndex(h => h === index ? null : h) }}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    if (isNonClickable || !poiData) return
                                    track('marker_clicked', {
                                        poiName: poi.name,
                                        category: poiData.category,
                                    })
                                    window.dispatchEvent(new CustomEvent('poi-selected', {
                                        detail: {
                                            poiName: poi.name,
                                            category: poiData.category,
                                            products: poiData.products || [],
                                        }
                                    }))
                                }}
                            >
                                <div className={`sec-marker-wrapper ${isHovered ? 'hovered' : ''} ${isSelected ? 'selected' : ''}`} style={isNonClickable ? { paddingLeft: '12px', paddingRight: '12px' } : {}}>
                                    <span className={`sec-label textButton${(deviceType === 'phone' && !isNonClickable) ? ' sec006-label' : ''}`}>
                                        {poiData?.products?.[0]?.name || poiData?.category || poi.name}
                                    </span>
                                    {!isNonClickable && (
                                        deviceType === 'phone'
                                            ? <div className="textButtonArrow textButtonArrow--blue sec006-chevron" />
                                            : <div className="textButtonArrow arrowButton" />
                                    )}
                                </div>
                            </div>
                        </Html>
                    </group>
                )
            })}
        </>
    )
}
